import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, PageHeader, Button, Spinner, Badge } from "@/components/ui";
import { Download, FileText, FileCheck2, CheckCircle2 } from "lucide-react";

const MD_URL = "/docs/GoMCO-Trillium-RFP-Response.md";

async function downloadPdf() {
  try {
    const res = await api.get("/docs/rfp-response/pdf", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "GoMCO-Trillium-RFP-Response.pdf"; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("PDF export failed"); }
}

const COVERAGE = [
  ["§3.1", "Overview: company profile, NIST 800-53, NC Security Manual, SCRM, NCQA"],
  ["§3.2", "Business experience, HIPAA, all 19 capability areas, screenshots, BPO, security governance"],
  ["§3.3", "Architecture, APIs/SDKs, interoperability, SLA, RBAC, end-to-end 837/835 billing"],
  ["§3.4", "Implementation, support, training, release protocols"],
  ["§3.5", "Full pricing grid, PMPM tiers, 5-year cost summary"],
];

export default function RfpResponse() {
  const [md, setMd] = useState(null);

  useEffect(() => {
    fetch(MD_URL).then((r) => r.text()).then(setMd).catch(() => setMd("Failed to load response document."));
  }, []);

  return (
    <div className="vc-fade-up max-w-5xl">
      <PageHeader
        title="Trillium RFP — Full Submission"
        subtitle="Go MCO's complete, point-by-point response to the Trillium Health Resources Clinical Management Platform RFP — every question and requirement answered."
        actions={<div className="flex gap-2">
          <Button variant="outline" onClick={downloadPdf} data-testid="download-rfp-pdf"><FileText className="h-4 w-4" /> PDF</Button>
          <a href={MD_URL} download><Button data-testid="download-rfp-md"><Download className="h-4 w-4" /> Markdown</Button></a>
        </div>}
      />

      <Card className="p-6 mb-6 bg-brand-950 text-white border-0">
        <div className="flex items-center gap-2 mb-3"><FileCheck2 className="h-5 w-5 text-brand-300" /><h2 className="font-display text-xl font-bold">Requirement Coverage</h2></div>
        <p className="text-brand-100 text-sm mb-4 max-w-3xl">This submission answers every item in the RFP, in the RFP's own order, and closes with a full traceability matrix mapping each requirement to its answer.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {COVERAGE.map(([s, d]) => (
            <div key={s} className="flex items-start gap-2 rounded-md bg-white/5 p-3">
              <CheckCircle2 className="h-4 w-4 text-brand-300 mt-0.5 shrink-0" />
              <div><Badge tone="Low">{s}</Badge><p className="text-sm text-brand-100 mt-1">{d}</p></div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6 sm:p-10">
        {md === null ? <Spinner label="Loading submission…" /> : (
          <div className="rfp-prose" data-testid="rfp-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{md}</ReactMarkdown>
          </div>
        )}
      </Card>
    </div>
  );
}
